// src/pages/TermsPage.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="tp-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* HERO */}
      <section className="tp-hero">
        <button className="tp-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="tp-badge">Legal</div>
        <h1>Terms of <span className="grad">Service</span></h1>
        <p className="tp-updated">Last updated: February 2026</p>
      </section>

      {/* CONTENT */}
      <article className="tp-article">

        <section className="tp-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using JepTags' product authentication platform, website, mobile applications,
            APIs, and related services (collectively, the "Services"), you agree to be bound by these
            Terms of Service. If you do not agree, do not use the Services.
          </p>
        </section>

        <section className="tp-section">
          <h2>2. Description of Services</h2>
          <p>
            JepTags provides a product authentication and traceability platform that enables businesses
            to protect their products using NFC, RFID, and QR code technology. Services include tag
            provisioning and management, consumer-facing product verification, real-time analytics and
            reporting dashboards, lottery and consumer engagement tools, API access for system integration,
            and supply chain traceability features.
          </p>
        </section>

        <section className="tp-section">
          <h2>3. Account Registration</h2>
          <p>
            To access business features, you must register for an account. You agree to provide accurate
            and complete information, maintain the security of your account credentials, notify us
            immediately of any unauthorized access, and accept responsibility for all activities under
            your account. We reserve the right to suspend or terminate accounts that violate these terms.
          </p>
        </section>

        <section className="tp-section">
          <h2>4. Subscription &amp; Payments</h2>
          <p>
            Paid features are available through subscription plans (Starter, Professional, Enterprise).
            Subscriptions are billed monthly or annually as selected. You authorize us to charge your
            payment method on file. Prices may change with 30 days' notice. Refunds are handled on a
            case-by-case basis. Free trials do not require payment information unless specified.
          </p>
        </section>

        <section className="tp-section">
          <h2>5. Acceptable Use</h2>
          <p>
            You agree not to use the Services to violate any applicable laws or regulations, infringe on
            intellectual property rights, distribute malware or harmful code, attempt to gain unauthorized
            access to our systems, use the platform to authenticate counterfeit or illegal goods, or
            resell or redistribute the Services without authorization.
          </p>
        </section>

        <section className="tp-section">
          <h2>6. Intellectual Property</h2>
          <p>
            All content, features, and functionality of the Services — including software, text, graphics,
            logos, and APIs — are owned by JepTags and protected by intellectual property laws. You retain
            ownership of your product data and content uploaded to the platform. By using the Services,
            you grant us a limited license to process and display your content as necessary to provide
            the Services.
          </p>
        </section>

        <section className="tp-section">
          <h2>7. Data &amp; Privacy</h2>
          <p>
            Your use of the Services is also governed by our{" "}
            <Link to="/privacy">Privacy Policy</Link>. You are responsible for ensuring that your use of
            scan data and consumer information complies with applicable data protection laws in your
            jurisdiction.
          </p>
        </section>

        <section className="tp-section">
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, JepTags shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising from your use of the Services.
            Our total liability shall not exceed the amount you paid us in the 12 months preceding the
            claim. The Services are provided "as is" without warranties of any kind, express or implied.
          </p>
        </section>

        <section className="tp-section">
          <h2>9. Termination</h2>
          <p>
            Either party may terminate the agreement at any time. We may suspend or terminate your access
            immediately if you violate these Terms. Upon termination, your right to use the Services
            ceases. We will retain your data for 90 days after termination, after which it may be
            permanently deleted.
          </p>
        </section>

        <section className="tp-section">
          <h2>10. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of California, United States, without
            regard to conflict of law provisions. Any disputes shall be resolved in the courts of
            Santa Clara County, California.
          </p>
        </section>

        <section className="tp-section">
          <h2>11. Contact</h2>
          <p>For questions about these Terms of Service, contact us at:</p>
          <div className="tp-contact-card">
            <div className="tp-contact-name">JepTags, Inc.</div>
            <div>2635 Cunningham Ave, California, CA 95148</div>
            <div>Email: <a href="mailto:legal@jeptags.com">legal@jeptags.com</a></div>
            <div>Phone: <a href="tel:+18447374799">+1-844-737-4799</a></div>
          </div>
        </section>

        {/* Related links */}
        <div className="tp-related">
          <Link to="/privacy" className="tp-related-link">
            Privacy Policy
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </article>
    </div>
  );
}

const STYLES = `
.tp-root *,.tp-root *::before,.tp-root *::after{margin:0;padding:0;box-sizing:border-box}
.tp-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.tp-root a{color:var(--cyan);text-decoration:none}
.tp-root a:hover{color:var(--cyan-bright);text-decoration:underline}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

/* HERO */
.tp-hero{max-width:900px;margin:0 auto;padding:60px 24px 32px;text-align:center;position:relative}

.tp-back{position:absolute;top:60px;left:24px;display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.tp-back:hover{border-color:var(--cyan);color:var(--cyan)}
.tp-back svg{width:14px;height:14px}

.tp-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.tp-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:12px}
.tp-updated{color:rgba(224,240,240,0.4);font-size:14px}

/* ARTICLE */
.tp-article{max-width:820px;margin:0 auto;padding:24px 24px 100px}

.tp-section{padding:28px 0;border-bottom:1px solid rgba(0,229,204,0.06)}
.tp-section:last-of-type{border-bottom:none}
.tp-section h2{font-size:22px;font-weight:700;color:#e0f0f0;margin-bottom:14px;letter-spacing:-.3px}
.tp-section p{font-size:15px;line-height:1.75;color:rgba(224,240,240,0.6);margin-bottom:12px}
.tp-section p:last-child{margin-bottom:0}

/* CONTACT CARD */
.tp-contact-card{background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.03));border:1px solid rgba(0,229,204,0.15);border-radius:16px;padding:22px 26px;margin-top:16px;font-size:14px;line-height:1.9;color:rgba(224,240,240,0.65)}
.tp-contact-name{font-weight:700;color:#e0f0f0;font-size:15px;margin-bottom:4px}

/* RELATED */
.tp-related{margin-top:40px;padding-top:32px;border-top:1px solid rgba(0,229,204,0.06)}
.tp-related-link{display:inline-flex;align-items:center;gap:8px;padding:12px 24px;border-radius:10px;border:1px solid rgba(0,229,204,0.2);background:rgba(0,229,204,0.04);color:var(--cyan);font-weight:600;font-size:14px;transition:all .25s}
.tp-related-link:hover{background:rgba(0,229,204,0.1);border-color:var(--cyan);text-decoration:none;color:var(--cyan-bright)}
.tp-related-link svg{width:16px;height:16px;transition:transform .25s}
.tp-related-link:hover svg{transform:translateX(3px)}

@media(max-width:700px){
  .tp-back{position:static;margin-bottom:20px}
  .tp-hero{padding:40px 20px 24px}
}
`;
